<template>
  <div class="partner-slider" v-if="partners.length">
    <div class="slider-viewport" ref="viewport">
      <div
          class="slider-track"
          :style="trackStyle"
          @transitionend="handleTransitionEnd"
      >
        <a
            v-for="(partner, index) in renderedPartners"
            :href="partner.url"
            :key="`${partner.url}-${index}`"
            class="slide"
            :style="slideStyle"
            target="_blank"
            rel="noopener noreferrer"
            @click="handlePartnerClick(partner)"
        >
          <img
              :src="partner.src"
              :alt="partner.alt"
              :style="imageStyle"
              @load="scheduleImageHeightUpdate"
          >
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PartnersVue',
  data() {
    return {
      partners: [],
      currentSlide: 0,
      visibleCount: 1,
      minSlideWidth: 240,
      maxVisibleCount: 4,
      minDesktopVisibleCount: 2,
      desktopBreakpoint: 992,
      autoplayMs: 2200,
      autoplayTimer: null,
      transitionEnabled: true,
      minImageHeight: null,
      imageHeightTimer: null,
      repeatCycles: 30,
      startCycle: 10,
      resetThresholdCycles: 3
    };
  },
  computed: {
    renderedPartners() {
      if (!this.partners.length) return [];

      const repeated = [];
      for (let i = 0; i < this.repeatCycles; i += 1) {
        repeated.push(...this.partners);
      }
      return repeated;
    },
    trackStyle() {
      const movePercent = this.currentSlide * (100 / this.visibleCount);
      return {
        transform: `translateX(-${movePercent}%)`,
        transition: this.transitionEnabled ? 'transform 0.35s ease' : 'none'
      };
    },
    slideStyle() {
      return {
        width: `${100 / this.visibleCount}%`,
        minWidth: `${100 / this.visibleCount}%`
      };
    },
    imageStyle() {
      if (!this.minImageHeight) return {};
      return {
        maxHeight: `${this.minImageHeight}px`
      };
    }
  },
  mounted() {
    this.getPartners();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.stopAutoplay();

    if (this.imageHeightTimer) {
      clearTimeout(this.imageHeightTimer);
      this.imageHeightTimer = null;
    }
  },
  methods: {
    mapEntryModeToPartnerTargetGroup() {
      const entryMode = localStorage.getItem('m2vetEntryMode');

      if (entryMode === 'do właścicieli zwierząt') return 'clients';
      if (entryMode === 'do specjalistów') return 'specialists';
      return 'all_users';
    },
    getPartners() {
      const ob = {
        page: 1,
        limit: 30,
        target_group: this.mapEntryModeToPartnerTargetGroup()
      };

      axios.post('/PartnerSearch', ob)
          .then((res) => {
            this.partners = res.data.items.map((item) => ({
              id: item.id,
              src: item.logo_url,
              alt: item.name,
              url: item.url
            }));

            this.$nextTick(() => {
              this.updateVisibleCount();
              this.currentSlide = this.partners.length * this.startCycle;
              this.startAutoplay();
              this.scheduleImageHeightUpdate();
            });
          });
    },
    handleResize() {
      this.updateVisibleCount();
      this.scheduleImageHeightUpdate();
    },
    updateVisibleCount() {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      const viewportWidth = viewport.clientWidth || 0;
      const rawCount = Math.max(1, Math.floor(viewportWidth / this.minSlideWidth));
      const cappedCount = Math.min(this.maxVisibleCount, rawCount);
      const isDesktop = window.innerWidth >= this.desktopBreakpoint;

      this.visibleCount = isDesktop
          ? Math.max(this.minDesktopVisibleCount, cappedCount)
          : cappedCount;
    },
    scheduleImageHeightUpdate() {
      if (this.imageHeightTimer) {
        clearTimeout(this.imageHeightTimer);
      }

      this.imageHeightTimer = setTimeout(() => {
        this.updateMinImageHeight();
      }, 40);
    },
    updateMinImageHeight() {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      const images = viewport.querySelectorAll('.slide img');
      if (!images.length) return;

      let minHeight = Infinity;

      images.forEach((img) => {
        if (!img.complete || !img.naturalWidth) return;
        const height = img.getBoundingClientRect().height;
        if (height > 0 && height < minHeight) {
          minHeight = height;
        }
      });

      if (minHeight !== Infinity) {
        this.minImageHeight = Math.floor(minHeight);
      }
    },
    startAutoplay() {
      this.stopAutoplay();
      if (this.partners.length <= 1) return;

      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplayMs);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    nextSlide() {
      if (!this.partners.length) return;
      this.transitionEnabled = true;
      this.currentSlide += 1;
    },
    handlePartnerClick(partner) {
      if (partner && partner.id) {
        axios.post('/PartnerClick', { partner_id: partner.id })
            .catch(err => console.error('Błąd rejestrowania kliknięcia:', err));
      }
    },
    handleTransitionEnd() {
      if (!this.partners.length) return;

      const cycleLen = this.partners.length;
      const resetFrom = cycleLen * (this.repeatCycles - this.resetThresholdCycles);

      if (this.currentSlide < resetFrom) return;

      const normalizedIndex = this.currentSlide % cycleLen;
      const targetIndex = cycleLen * this.startCycle + normalizedIndex;

      this.transitionEnabled = false;
      this.currentSlide = targetIndex;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.transitionEnabled = true;
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
.partner-slider {
  width: 100%;
}

.slider-viewport {
  width: 100%;
  overflow: hidden;
}

.slider-track {
  display: flex;
  will-change: transform;
}

.slide {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
}

.slide img {
  width: 100%;
  max-width: 100%;
  object-fit: contain;
  opacity: 0.65;
  transition: 0.2s;

  &:hover {
    filter: grayscale(0);
    opacity: 1;
  }
}
</style>